import React, { useEffect, useState, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useCart } from '../contexts/CartContext';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { formatZAR } from '../utils/currency';
import { GiftingModal } from '../components/modals/GiftingModal';
import { ShoppingCart, Trash2, Plus, Minus, Calendar, MessageSquare, Clock, MapPin, Truck, Package, ChevronRight, CheckCircle2, Gift, User, Edit2, Phone, Check, AlertCircle, ArrowLeft, ArrowRight, MessageCircle } from 'lucide-react';
type CheckoutStep = 'cart' | 'configure-items' | 'summary';
const WHATSAPP_NUMBER = '27732736906';
export function CartPage() {
  const {
    cart,
    removeFromCart,
    updateQuantity,
    updateCartItem,
    cartTotal,
    clearCart
  } = useCart();
  const {
    addOrder
  } = useData();
  const {
    isAuthenticated,
    user
  } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('cart');
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [activeItemForGifting, setActiveItemForGifting] = useState<string | null>(null);
  const [isWithinHeidelberg, setIsWithinHeidelberg] = useState(true);
  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);
  const currentItem = cart[currentItemIndex];
  const allItemsConfigured = cart.every(item => item.isConfigured);
  const configuredCount = cart.filter(item => item.isConfigured).length;
  const handleAskAboutProduct = (productName: string) => {
    const message = `Hi! I have ${productName} in my cart and I'd like to know more details about it.`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };
  // Calculate delivery fee based on unique delivery addresses
  const calculateDeliveryFee = () => {
    const uniqueAddresses = new Set(cart.filter(item => item.deliveryType !== 'collection').map(item => item.deliveryAddress || 'main'));
    if (uniqueAddresses.size === 0) return 0;
    // Base fee for first delivery
    let fee = isWithinHeidelberg ? 75 : 150;
    // Check if any delivery is after 19:00 (7 PM)
    const hasAfterHoursDelivery = cart.some(item => {
      if (item.deliveryType === 'collection' || !item.preferredTime) return false;
      const [hours] = item.preferredTime.split(':').map(Number);
      return hours >= 19;
    });
    // Double the fee if delivery is after 19:00
    if (hasAfterHoursDelivery) {
      fee = fee * 2;
    }
    // Additional fee for each extra delivery address
    if (uniqueAddresses.size > 1) {
      fee += (uniqueAddresses.size - 1) * 50;
    }
    return fee;
  };
  const deliveryFee = calculateDeliveryFee();
  const totalWithDelivery = cartTotal + deliveryFee;
  const handleStartConfiguration = () => {
    setCurrentStep('configure-items');
    setCurrentItemIndex(0);
  };
  const handleNextItem = () => {
    if (!currentItem) return;
    // Mark current item as configured
    updateCartItem(currentItem.product.id, {
      isConfigured: true
    });
    // Move to next item or summary
    if (currentItemIndex < cart.length - 1) {
      setCurrentItemIndex(currentItemIndex + 1);
    } else {
      setCurrentStep('summary');
    }
  };
  const handlePreviousItem = () => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
    } else {
      setCurrentStep('cart');
    }
  };
  const handleEditItem = (index: number) => {
    setCurrentItemIndex(index);
    setCurrentStep('configure-items');
  };
  const handlePlaceOrder = () => {
    if (!user) return;
    // Navigate to checkout page with order details
    navigate('/checkout', {
      state: {
        cartTotal,
        deliveryFee,
        totalWithDelivery,
        isWithinHeidelberg
      }
    });
  };
  const isCurrentItemValid = () => {
    if (!currentItem) return false;
    // Must have delivery date and time
    if (!currentItem.deliveryDate || !currentItem.preferredTime) return false;
    // If delivery (not collection), must have address
    if (currentItem.deliveryType === 'delivery' && !currentItem.deliveryAddress) {
      return false;
    }
    // If gift, must have recipient details
    if (currentItem.isGift && (!currentItem.recipientName || !currentItem.recipientPhone)) {
      return false;
    }
    return true;
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-1722">
      <Navbar data-id="element-1723" />

      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1724">
        {/* Progress Header */}
        <div className="mb-8" data-id="element-1725">
          <div className="max-w-3xl mx-auto" data-id="element-1726">
            {currentStep === 'configure-items' && <div className="mb-6" data-id="element-1727">
                <div className="flex items-center justify-between mb-2" data-id="element-1728">
                  <span className="text-sm font-medium text-gray-600" data-id="element-1729">
                    Configuring Item {currentItemIndex + 1} of {cart.length}
                  </span>
                  <span className="text-sm font-medium text-rose-600" data-id="element-1730">
                    {configuredCount} of {cart.length} completed
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1731">
                  <div className="bg-rose-500 h-2 rounded-full transition-all duration-500" style={{
                width: `${configuredCount / cart.length * 100}%`
              }} data-id="element-1732" />
                </div>
              </div>}

            <div className="flex items-center justify-center gap-4" data-id="element-1733">
              <div className={`flex items-center gap-2 ${currentStep === 'cart' ? 'text-rose-600' : 'text-gray-400'}`} data-id="element-1734">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'cart' ? 'bg-rose-500 text-white' : 'bg-gray-200'}`} data-id="element-1735">
                  <ShoppingCart className="w-4 h-4" data-id="element-1736" />
                </div>
                <span className="text-sm font-medium hidden sm:inline" data-id="element-1737">
                  Cart
                </span>
              </div>

              <div className="flex-1 h-1 bg-gray-200 max-w-[100px]" data-id="element-1738">
                <div className={`h-full ${currentStep !== 'cart' ? 'bg-rose-500' : 'bg-gray-200'}`} data-id="element-1739" />
              </div>

              <div className={`flex items-center gap-2 ${currentStep === 'configure-items' ? 'text-rose-600' : 'text-gray-400'}`} data-id="element-1740">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'configure-items' ? 'bg-rose-500 text-white' : currentStep === 'summary' ? 'bg-green-500 text-white' : 'bg-gray-200'}`} data-id="element-1741">
                  {currentStep === 'summary' ? <Check className="w-4 h-4" data-id="element-1742" /> : <MessageSquare className="w-4 h-4" data-id="element-1743" />}
                </div>
                <span className="text-sm font-medium hidden sm:inline" data-id="element-1744">
                  Configure
                </span>
              </div>

              <div className="flex-1 h-1 bg-gray-200 max-w-[100px]" data-id="element-1745">
                <div className={`h-full ${currentStep === 'summary' ? 'bg-rose-500' : 'bg-gray-200'}`} data-id="element-1746" />
              </div>

              <div className={`flex items-center gap-2 ${currentStep === 'summary' ? 'text-rose-600' : 'text-gray-400'}`} data-id="element-1747">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'summary' ? 'bg-rose-500 text-white' : 'bg-gray-200'}`} data-id="element-1748">
                  <CheckCircle2 className="w-4 h-4" data-id="element-1749" />
                </div>
                <span className="text-sm font-medium hidden sm:inline" data-id="element-1750">
                  Review
                </span>
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait" data-id="element-1751">
          {/* Cart View */}
          {currentStep === 'cart' && <motion.div key="cart" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} data-id="element-1752">
              <div className="max-w-4xl mx-auto" data-id="element-1753">
                <h1 className="font-serif text-3xl font-bold text-gray-900 mb-2" data-id="element-1754">
                  Shopping Cart
                </h1>
                <p className="text-gray-600 mb-8" data-id="element-1755">
                  Review your items before configuring delivery details
                </p>

                {cart.length === 0 ? <div className="text-center py-20 bg-white rounded-2xl shadow-sm" data-id="element-1756">
                    <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-1757" />
                    <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2" data-id="element-1758">
                      Your cart is empty
                    </h2>
                    <p className="text-gray-600 mb-6" data-id="element-1759">
                      Add some beautiful gifts to get started!
                    </p>
                    <Button onClick={() => navigate('/')} data-id="element-1760">
                      Browse Products
                    </Button>
                  </div> : <>
                    <div className="space-y-4 mb-8" data-id="element-1761">
                      {cart.map((item, index) => <motion.div key={item.product.id} layout className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1762">
                          <div className="flex items-start gap-6" data-id="element-1763">
                            <div className="relative" data-id="element-1764">
                              <img src={item.product.image} alt={item.product.name} className="w-24 h-24 object-cover rounded-lg" data-id="element-1765" />
                              {item.isConfigured && <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center" data-id="element-1766">
                                  <Check className="w-4 h-4 text-white" data-id="element-1767" />
                                </div>}
                            </div>

                            <div className="flex-1" data-id="element-1768">
                              <div className="flex justify-between items-start mb-2" data-id="element-1769">
                                <div data-id="element-1770">
                                  <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1771">
                                    {item.product.name}
                                  </h3>
                                  <p className="text-sm text-gray-500" data-id="element-1772">
                                    {item.product.category}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center justify-between mt-4" data-id="element-1773">
                                <div className="flex items-center gap-3" data-id="element-1774">
                                  <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50" data-id="element-1775">
                                    <Minus className="w-4 h-4" data-id="element-1776" />
                                  </button>
                                  <span className="w-8 text-center font-semibold" data-id="element-1777">
                                    {item.quantity}
                                  </span>
                                  <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50" data-id="element-1778">
                                    <Plus className="w-4 h-4" data-id="element-1779" />
                                  </button>
                                </div>

                                <div className="flex items-center gap-2" data-id="element-1780">
                                  {/* WhatsApp Info Button */}
                                  <button onClick={() => handleAskAboutProduct(item.product.name)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors group relative" title="Ask about this product" data-id="element-1781">
                                    <MessageCircle className="w-5 h-5" data-id="element-1782" />
                                    <span className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none" data-id="element-1783">
                                      Ask on WhatsApp
                                    </span>
                                  </button>

                                  {item.isConfigured ? <span className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm font-medium" data-id="element-1784">
                                      <Check className="w-4 h-4" data-id="element-1785" />
                                      Configured
                                    </span> : <span className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg text-sm font-medium" data-id="element-1786">
                                      <AlertCircle className="w-4 h-4" data-id="element-1787" />
                                      Needs Setup
                                    </span>}
                                  <button onClick={() => removeFromCart(item.product.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-id="element-1788">
                                    <Trash2 className="w-5 h-5" data-id="element-1789" />
                                  </button>
                                </div>
                              </div>

                              {item.isConfigured && <div className="mt-3 text-sm bg-gray-50 p-3 rounded-lg" data-id="element-1790">
                                  <div className="flex items-center gap-2 text-gray-600 mb-1" data-id="element-1791">
                                    {item.isGift ? <Gift className="w-4 h-4" data-id="element-1792" /> : <User className="w-4 h-4" data-id="element-1793" />}
                                    <span className="font-medium" data-id="element-1794">
                                      {item.isGift ? `Gift for ${item.recipientName}` : 'For Myself'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-gray-600" data-id="element-1795">
                                    <Calendar className="w-4 h-4" data-id="element-1796" />
                                    <span data-id="element-1797">{item.deliveryDate}</span>
                                  </div>
                                </div>}
                            </div>
                          </div>
                        </motion.div>)}
                    </div>

                    <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1798">
                      <div className="flex justify-between items-center mb-6" data-id="element-1799">
                        <span className="text-lg font-semibold text-gray-900" data-id="element-1800">
                          {cart.length} {cart.length === 1 ? 'Item' : 'Items'}{' '}
                          in Cart
                        </span>
                        <span className="text-sm text-gray-500" data-id="element-1801">
                          Price shown at checkout
                        </span>
                      </div>
                      <Button onClick={handleStartConfiguration} className="w-full" size="lg" rightIcon={<ChevronRight data-id="element-1803" />} data-id="element-1802">
                        Configure Items
                      </Button>

                      {/* Help Text */}
                      <p className="text-xs text-gray-500 text-center mt-4" data-id="element-1804">
                        Need help? Click the{' '}
                        <MessageCircle className="w-3 h-3 inline text-green-600" data-id="element-1805" />{' '}
                        icon on any product to ask us on WhatsApp
                      </p>
                    </div>
                  </>}
              </div>
            </motion.div>}

          {/* Item Configuration */}
          {currentStep === 'configure-items' && currentItem && <motion.div key={`configure-${currentItemIndex}`} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="max-w-2xl mx-auto" data-id="element-1806">
              <div className="bg-white rounded-2xl p-8 shadow-sm" data-id="element-1807">
                {/* Item Header */}
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100" data-id="element-1808">
                  <img src={currentItem.product.image} alt={currentItem.product.name} className="w-20 h-20 object-cover rounded-lg" data-id="element-1809" />
                  <div className="flex-1" data-id="element-1810">
                    <h2 className="font-serif text-2xl font-bold text-gray-900" data-id="element-1811">
                      {currentItem.product.name}
                    </h2>
                    <p className="text-gray-600" data-id="element-1812">
                      Quantity: {currentItem.quantity} •{' '}
                      {currentItem.product.category}
                    </p>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-1813">
                  {/* Gifting Selection */}
                  <div data-id="element-1814">
                    <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1815">
                      Who is this for? *
                    </label>
                    <button onClick={() => setActiveItemForGifting(currentItem.product.id)} className={`w-full p-4 border-2 rounded-lg transition-all text-left ${currentItem.isGift !== undefined ? 'border-rose-500 bg-rose-50' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-1816">
                      <div className="flex items-center justify-between" data-id="element-1817">
                        <div className="flex items-center gap-3" data-id="element-1818">
                          {currentItem.isGift ? <Gift className="w-5 h-5 text-rose-600" data-id="element-1819" /> : currentItem.isGift === false ? <User className="w-5 h-5 text-rose-600" data-id="element-1820" /> : <AlertCircle className="w-5 h-5 text-gray-400" data-id="element-1821" />}
                          <div data-id="element-1822">
                            <p className="font-medium text-gray-900" data-id="element-1823">
                              {currentItem.isGift ? `Gift for ${currentItem.recipientName}` : currentItem.isGift === false ? 'For Myself' : 'Choose recipient'}
                            </p>
                            {currentItem.isGift && currentItem.recipientPhone && <p className="text-sm text-gray-600" data-id="element-1824">
                                  {currentItem.recipientPhone}
                                </p>}
                          </div>
                        </div>
                        <Edit2 className="w-4 h-4 text-gray-400" data-id="element-1825" />
                      </div>
                    </button>
                  </div>

                  {/* Custom Message */}
                  <div data-id="element-1826">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1827">
                      Special Message (Optional)
                    </label>
                    <textarea value={currentItem.customMessage || ''} onChange={e => updateCartItem(currentItem.product.id, {
                  customMessage: e.target.value
                })} placeholder="Add a personal note..." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent" rows={3} data-id="element-1828" />
                  </div>

                  {/* Delivery Date & Time */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1829">
                    <div data-id="element-1830">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1831">
                        Delivery Date *
                      </label>
                      <Input type="date" value={currentItem.deliveryDate || ''} onChange={e => updateCartItem(currentItem.product.id, {
                    deliveryDate: e.target.value
                  })} min={new Date().toISOString().split('T')[0]} required data-id="element-1832" />
                    </div>
                    <div data-id="element-1833">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1834">
                        Preferred Time *
                      </label>
                      <Input type="time" value={currentItem.preferredTime || ''} onChange={e => updateCartItem(currentItem.product.id, {
                    preferredTime: e.target.value
                  })} required data-id="element-1835" />
                    </div>
                  </div>

                  {/* Delivery Type */}
                  <div data-id="element-1836">
                    <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1837">
                      Delivery Method *
                    </label>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1838">
                      <button onClick={() => updateCartItem(currentItem.product.id, {
                    deliveryType: 'delivery'
                  })} className={`p-4 border-2 rounded-lg transition-all ${currentItem.deliveryType === 'delivery' ? 'border-rose-500 bg-rose-50' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-1839">
                        <Truck className="w-6 h-6 mx-auto mb-2 text-rose-500" data-id="element-1840" />
                        <div className="font-semibold" data-id="element-1841">Delivery</div>
                      </button>
                      <button onClick={() => updateCartItem(currentItem.product.id, {
                    deliveryType: 'collection',
                    deliveryAddress: undefined
                  })} className={`p-4 border-2 rounded-lg transition-all ${currentItem.deliveryType === 'collection' ? 'border-rose-500 bg-rose-50' : 'border-gray-300 hover:border-gray-400'}`} data-id="element-1842">
                        <Package className="w-6 h-6 mx-auto mb-2 text-rose-500" data-id="element-1843" />
                        <div className="font-semibold" data-id="element-1844">Collection</div>
                      </button>
                    </div>
                  </div>

                  {/* Delivery Address (if delivery selected) */}
                  {currentItem.deliveryType === 'delivery' && <div data-id="element-1845">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1846">
                        Delivery Address *
                      </label>
                      <Input type="text" value={currentItem.deliveryAddress || ''} onChange={e => updateCartItem(currentItem.product.id, {
                  deliveryAddress: e.target.value
                })} placeholder="Enter full delivery address" required data-id="element-1847" />
                    </div>}
                </div>

                {/* Navigation */}
                <div className="flex gap-4 mt-8 pt-6 border-t border-gray-100" data-id="element-1848">
                  <Button variant="outline" onClick={handlePreviousItem} leftIcon={<ArrowLeft data-id="element-1850" />} data-id="element-1849">
                    {currentItemIndex === 0 ? 'Back to Cart' : 'Previous Item'}
                  </Button>
                  <Button onClick={handleNextItem} disabled={!isCurrentItemValid()} className="flex-1" rightIcon={currentItemIndex === cart.length - 1 ? <CheckCircle2 data-id="element-1852" /> : <ArrowRight data-id="element-1853" />} data-id="element-1851">
                    {currentItemIndex === cart.length - 1 ? 'Review Order' : 'Next Item'}
                  </Button>
                </div>
              </div>
            </motion.div>}

          {/* Final Summary with Pricing */}
          {currentStep === 'summary' && <motion.div key="summary" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="max-w-3xl mx-auto" data-id="element-1854">
              <div className="bg-white rounded-2xl p-8 shadow-sm" data-id="element-1855">
                <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2" data-id="element-1856">
                  Review Your Order
                </h2>
                <p className="text-gray-600 mb-8" data-id="element-1857">
                  Final pricing and delivery details
                </p>

                <div className="space-y-6 mb-8" data-id="element-1858">
                  {cart.map((item, index) => <div key={item.product.id} className="border border-gray-200 rounded-xl p-6" data-id="element-1859">
                      <div className="flex justify-between items-start mb-4" data-id="element-1860">
                        <div className="flex gap-4" data-id="element-1861">
                          <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center text-rose-600 font-bold" data-id="element-1862">
                            {index + 1}
                          </div>
                          <div className="flex-1" data-id="element-1863">
                            <h3 className="font-bold text-gray-900 mb-1" data-id="element-1864">
                              {item.product.name}
                            </h3>
                            <p className="text-sm text-gray-600 mb-3" data-id="element-1865">
                              Qty: {item.quantity} ×{' '}
                              {formatZAR(item.product.price)} ={' '}
                              {formatZAR(item.product.price * item.quantity)}
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-id="element-1866">
                              <div className="space-y-2" data-id="element-1867">
                                <div className="flex items-center gap-2 text-gray-600" data-id="element-1868">
                                  {item.isGift ? <Gift className="w-4 h-4" data-id="element-1869" /> : <User className="w-4 h-4" data-id="element-1870" />}
                                  <span className="font-medium" data-id="element-1871">
                                    {item.isGift ? `Gift for ${item.recipientName}` : 'For Myself'}
                                  </span>
                                </div>
                                {item.isGift && <div className="flex items-center gap-2 text-gray-600" data-id="element-1872">
                                    <Phone className="w-4 h-4" data-id="element-1873" />
                                    <span data-id="element-1874">{item.recipientPhone}</span>
                                  </div>}
                                <div className="flex items-center gap-2 text-gray-600" data-id="element-1875">
                                  <Calendar className="w-4 h-4" data-id="element-1876" />
                                  <span data-id="element-1877">
                                    {item.deliveryDate} at {item.preferredTime}
                                  </span>
                                </div>
                              </div>

                              <div className="space-y-2" data-id="element-1878">
                                <div className="flex items-center gap-2 text-gray-600" data-id="element-1879">
                                  {item.deliveryType === 'collection' ? <Package className="w-4 h-4" data-id="element-1880" /> : <Truck className="w-4 h-4" data-id="element-1881" />}
                                  <span className="capitalize" data-id="element-1882">
                                    {item.deliveryType}
                                  </span>
                                </div>
                                {item.deliveryType === 'delivery' && <div className="flex items-start gap-2 text-gray-600" data-id="element-1883">
                                    <MapPin className="w-4 h-4 mt-0.5" data-id="element-1884" />
                                    <span data-id="element-1885">{item.deliveryAddress}</span>
                                  </div>}
                                {item.customMessage && <div className="flex items-start gap-2 text-gray-600 italic" data-id="element-1886">
                                    <MessageSquare className="w-4 h-4 mt-0.5" data-id="element-1887" />
                                    <span className="text-xs" data-id="element-1888">
                                      "{item.customMessage}"
                                    </span>
                                  </div>}
                              </div>
                            </div>
                          </div>
                        </div>
                        <button onClick={() => handleEditItem(index)} className="text-rose-500 hover:text-rose-600 text-sm font-medium flex items-center gap-1" data-id="element-1889">
                          <Edit2 className="w-4 h-4" data-id="element-1890" />
                          Edit
                        </button>
                      </div>
                    </div>)}
                </div>

                {/* Distance Selection */}
                <div className="mb-8 p-6 bg-gray-50 rounded-xl" data-id="element-1891">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1892">
                    Distance from Heidelberg
                  </label>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1893">
                    <button onClick={() => setIsWithinHeidelberg(true)} className={`p-4 border-2 rounded-lg transition-all ${isWithinHeidelberg ? 'border-rose-500 bg-white' : 'border-gray-300 hover:border-gray-400 bg-white'}`} data-id="element-1894">
                      <div className="font-semibold mb-1" data-id="element-1895">Within 15km</div>
                      <div className="text-sm text-gray-600" data-id="element-1896">
                        R75 delivery fee
                      </div>
                    </button>
                    <button onClick={() => setIsWithinHeidelberg(false)} className={`p-4 border-2 rounded-lg transition-all ${!isWithinHeidelberg ? 'border-rose-500 bg-white' : 'border-gray-300 hover:border-gray-400 bg-white'}`} data-id="element-1897">
                      <div className="font-semibold mb-1" data-id="element-1898">
                        Outside Heidelberg
                      </div>
                      <div className="text-sm text-gray-600" data-id="element-1899">
                        R150 delivery fee
                      </div>
                    </button>
                  </div>
                </div>

                {/* Price Breakdown */}
                <div className="border-t border-gray-200 pt-6 space-y-3" data-id="element-1900">
                  <div className="flex justify-between text-gray-600" data-id="element-1901">
                    <span data-id="element-1902">Subtotal ({cart.length} items)</span>
                    <span className="font-medium" data-id="element-1903">{formatZAR(cartTotal)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600" data-id="element-1904">
                    <span data-id="element-1905">Delivery Fee</span>
                    <span className="font-medium" data-id="element-1906">
                      {formatZAR(deliveryFee)}
                    </span>
                  </div>
                  {cart.some(item => {
                if (item.deliveryType === 'collection' || !item.preferredTime) return false;
                const [hours] = item.preferredTime.split(':').map(Number);
                return hours >= 19;
              }) && <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm" data-id="element-1907">
                      <Clock className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" data-id="element-1908" />
                      <div data-id="element-1909">
                        <p className="font-medium text-yellow-900" data-id="element-1910">
                          After Hours Delivery
                        </p>
                        <p className="text-yellow-700" data-id="element-1911">
                          Delivery after 19:00 (7 PM) incurs double the standard
                          delivery fee.
                        </p>
                      </div>
                    </div>}
                  <div className="flex justify-between text-xl font-bold pt-3 border-t border-gray-200" data-id="element-1912">
                    <span data-id="element-1913">Total</span>
                    <span className="text-rose-500" data-id="element-1914">
                      {formatZAR(totalWithDelivery)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-4 mt-8" data-id="element-1915">
                  <Button variant="outline" onClick={() => setCurrentStep('configure-items')} leftIcon={<ArrowLeft data-id="element-1917" />} data-id="element-1916">
                    Back
                  </Button>
                  <Button onClick={handlePlaceOrder} className="flex-1" size="lg" rightIcon={<CheckCircle2 data-id="element-1919" />} data-id="element-1918">
                    Place Order - {formatZAR(totalWithDelivery)}
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <GiftingModal isOpen={!!activeItemForGifting} onClose={() => setActiveItemForGifting(null)} productName={cart.find(i => i.product.id === activeItemForGifting)?.product.name || ''} onConfirm={details => {
      if (activeItemForGifting) {
        updateCartItem(activeItemForGifting, details);
      }
    }} data-id="element-1920" />

      <Footer data-id="element-1921" />
    </div>;
}